/**
 * Analysis Config Constants
 * quality-gate-exempt: pre-modularization
 */

import type { AnalysisMode } from '@views/picks/constants';
import type { PropTypeOption } from './configTypes';

export const MODE_OPTIONS: Array<{ value: AnalysisMode; label: string; desc: string }> = [
  {
    value: 'standard',
    label: 'Standard',
    desc: 'Quick picks with core analysis',
  },
  {
    value: 'detailed',
    label: 'Detailed',
    desc: 'Adds leg-by-leg breakdown and deeper reasoning',
  },
  {
    value: 'premium',
    label: 'Premium',
    desc: 'Most thorough analysis with advanced risk assessment',
  },
];

export const TIER_COLORS: Record<string, { active: string; dim: string; text: string }> = {
  safe: {
    active: 'border-theme-success bg-theme-success/30',
    dim: 'border-theme-border bg-theme-surface/30',
    text: 'text-theme-success',
  },
  value: {
    active: 'border-theme-primary bg-theme-primary/30',
    dim: 'border-theme-border bg-theme-surface/30',
    text: 'text-theme-primary',
  },
  upside: {
    active: 'border-theme-warning bg-theme-warning/30',
    dim: 'border-theme-border bg-theme-surface/30',
    text: 'text-theme-warning',
  },
  long: {
    active: 'border-theme-danger bg-theme-danger/30',
    dim: 'border-theme-border bg-theme-surface/30',
    text: 'text-theme-danger',
  },
  lottery: {
    active: 'border-purple-500 bg-purple-900/30',
    dim: 'border-theme-border bg-theme-surface/30',
    text: 'text-purple-400',
  },
};

export const PROP_TYPE_OPTIONS: PropTypeOption[] = [
  // ── Game markets ──
  { key: 'h2h', label: 'Moneyline', description: 'Game winner bets', category: 'Game' },
  {
    key: 'spreads',
    label: 'Spreads',
    description: 'Point spread bets (e.g., Lakers -5.5)',
    category: 'Game',
  },
  {
    key: 'totals',
    label: 'Game Totals',
    description: 'Over/under game total points',
    category: 'Game',
  },
  {
    key: 'alternate_spreads',
    label: 'Alt Spreads',
    description: 'Alternate point spread lines',
    category: 'Game',
  },
  {
    key: 'alternate_totals',
    label: 'Alt Totals',
    description: 'Alternate game total lines',
    category: 'Game',
  },
  // ── Team markets ──
  {
    key: 'team_totals',
    label: 'Team Total',
    description: 'Individual team over/under points',
    category: 'Team',
  },
  {
    key: 'alternate_team_totals',
    label: 'Alt Team Total',
    description: 'Alternate team total lines',
    category: 'Team',
  },
  // ── Player props ──
  {
    key: 'player_points',
    label: 'Points',
    description: 'Player scoring props',
    category: 'Player',
  },
  {
    key: 'player_rebounds',
    label: 'Rebounds',
    description: 'Player rebounding props',
    category: 'Player',
  },
  {
    key: 'player_assists',
    label: 'Assists',
    description: 'Player assist props',
    category: 'Player',
  },
  {
    key: 'player_threes',
    label: '3-Pointers',
    description: 'Three-point makes',
    category: 'Player',
  },
  {
    key: 'player_blocks',
    label: 'Blocks',
    description: 'Player block props',
    category: 'Player',
  },
  {
    key: 'player_steals',
    label: 'Steals',
    description: 'Player steal props',
    category: 'Player',
  },
  {
    key: 'player_turnovers',
    label: 'Turnovers',
    description: 'Player turnover props',
    category: 'Player',
  },
  // ── Shooting props ──
  {
    key: 'player_field_goals',
    label: 'Field Goals',
    description: 'Field goals made',
    category: 'Shooting',
  },
  {
    key: 'player_frees_made',
    label: 'Free Throws',
    description: 'Free throws made',
    category: 'Shooting',
  },
  // ── Combo props ──
  {
    key: 'player_blocks_steals',
    label: 'BLK+STL',
    description: 'Blocks plus steals',
    category: 'Combo',
  },
  {
    key: 'player_points_rebounds_assists',
    label: 'PTS+REB+AST',
    description: 'Combined stat props',
    category: 'Combo',
  },
  {
    key: 'player_points_rebounds',
    label: 'PTS+REB',
    description: 'Points plus rebounds',
    category: 'Combo',
  },
  {
    key: 'player_points_assists',
    label: 'PTS+AST',
    description: 'Points plus assists',
    category: 'Combo',
  },
  {
    key: 'player_rebounds_assists',
    label: 'REB+AST',
    description: 'Rebounds plus assists',
    category: 'Combo',
  },
  // ── Special props ──
  {
    key: 'player_double_double',
    label: 'Double-Double',
    description: 'Yes/No - 10+ in two stat categories',
    category: 'Special',
  },
  {
    key: 'player_triple_double',
    label: 'Triple-Double',
    description: 'Yes/No - 10+ in three stat categories',
    category: 'Special',
  },
  {
    key: 'player_first_basket',
    label: 'First Basket',
    description: 'First player to score in game',
    category: 'Special',
  },
  {
    key: 'player_first_team_basket',
    label: 'First Team Basket',
    description: 'First team to score in game',
    category: 'Special',
  },
  // ── Alternate player props ──
  {
    key: 'player_points_alternate',
    label: 'Alt Points',
    description: 'Alternate points lines',
    category: 'Alt Player',
  },
  {
    key: 'player_rebounds_alternate',
    label: 'Alt Rebounds',
    description: 'Alternate rebounds lines',
    category: 'Alt Player',
  },
  {
    key: 'player_assists_alternate',
    label: 'Alt Assists',
    description: 'Alternate assists lines',
    category: 'Alt Player',
  },
  {
    key: 'player_threes_alternate',
    label: 'Alt 3-Pointers',
    description: 'Alternate three-point lines',
    category: 'Alt Player',
  },
  {
    key: 'player_steals_alternate',
    label: 'Alt Steals',
    description: 'Alternate steals lines',
    category: 'Alt Player',
  },
  {
    key: 'player_blocks_alternate',
    label: 'Alt Blocks',
    description: 'Alternate blocks lines',
    category: 'Alt Player',
  },
  {
    key: 'player_turnovers_alternate',
    label: 'Alt Turnovers',
    description: 'Alternate turnovers lines',
    category: 'Alt Player',
  },
  {
    key: 'player_field_goals_alternate',
    label: 'Alt Field Goals',
    description: 'Alternate field goals lines',
    category: 'Alt Player',
  },
  {
    key: 'player_blocks_steals_alternate',
    label: 'Alt BLK+STL',
    description: 'Alternate blocks+steals lines',
    category: 'Alt Player',
  },
  {
    key: 'player_points_rebounds_assists_alternate',
    label: 'Alt PTS+REB+AST',
    description: 'Alternate PRA lines',
    category: 'Alt Player',
  },
  {
    key: 'player_points_rebounds_alternate',
    label: 'Alt PTS+REB',
    description: 'Alternate points+rebounds lines',
    category: 'Alt Player',
  },
  {
    key: 'player_points_assists_alternate',
    label: 'Alt PTS+AST',
    description: 'Alternate points+assists lines',
    category: 'Alt Player',
  },
  {
    key: 'player_rebounds_assists_alternate',
    label: 'Alt REB+AST',
    description: 'Alternate rebounds+assists lines',
    category: 'Alt Player',
  },
  // ── Half markets ──
  { key: 'h2h_h1', label: '1H Moneyline', description: 'First half winner', category: 'Half' },
  { key: 'h2h_h2', label: '2H Moneyline', description: 'Second half winner', category: 'Half' },
  {
    key: 'spreads_h1',
    label: '1H Spread',
    description: 'First half point spread',
    category: 'Half',
  },
  {
    key: 'spreads_h2',
    label: '2H Spread',
    description: 'Second half point spread',
    category: 'Half',
  },
  { key: 'totals_h1', label: '1H Total', description: 'First half over/under', category: 'Half' },
  {
    key: 'totals_h2',
    label: '2H Total',
    description: 'Second half over/under',
    category: 'Half',
  },
  {
    key: 'team_totals_h1',
    label: '1H Team Total',
    description: 'First half team points',
    category: 'Half',
  },
  {
    key: 'team_totals_h2',
    label: '2H Team Total',
    description: 'Second half team points',
    category: 'Half',
  },
  {
    key: 'alternate_spreads_h1',
    label: '1H Alt Spread',
    description: 'First half alternate spread',
    category: 'Half',
  },
  {
    key: 'alternate_spreads_h2',
    label: '2H Alt Spread',
    description: 'Second half alternate spread',
    category: 'Half',
  },
  {
    key: 'alternate_totals_h1',
    label: '1H Alt Total',
    description: 'First half alternate total',
    category: 'Half',
  },
  {
    key: 'alternate_totals_h2',
    label: '2H Alt Total',
    description: 'Second half alternate total',
    category: 'Half',
  },
  {
    key: 'alternate_team_totals_h1',
    label: '1H Alt Team Total',
    description: 'First half alternate team total',
    category: 'Half',
  },
  {
    key: 'alternate_team_totals_h2',
    label: '2H Alt Team Total',
    description: 'Second half alternate team total',
    category: 'Half',
  },
  // ── Quarter markets ──
  {
    key: 'h2h_q1',
    label: 'Q1 Moneyline',
    description: 'First quarter winner',
    category: 'Quarter',
  },
  {
    key: 'h2h_q2',
    label: 'Q2 Moneyline',
    description: 'Second quarter winner',
    category: 'Quarter',
  },
  {
    key: 'h2h_q3',
    label: 'Q3 Moneyline',
    description: 'Third quarter winner',
    category: 'Quarter',
  },
  {
    key: 'h2h_q4',
    label: 'Q4 Moneyline',
    description: 'Fourth quarter winner',
    category: 'Quarter',
  },
  {
    key: 'spreads_q1',
    label: 'Q1 Spread',
    description: 'First quarter spread',
    category: 'Quarter',
  },
  {
    key: 'spreads_q2',
    label: 'Q2 Spread',
    description: 'Second quarter spread',
    category: 'Quarter',
  },
  {
    key: 'spreads_q3',
    label: 'Q3 Spread',
    description: 'Third quarter spread',
    category: 'Quarter',
  },
  {
    key: 'spreads_q4',
    label: 'Q4 Spread',
    description: 'Fourth quarter spread',
    category: 'Quarter',
  },
  {
    key: 'totals_q1',
    label: 'Q1 Total',
    description: 'First quarter over/under',
    category: 'Quarter',
  },
  {
    key: 'totals_q2',
    label: 'Q2 Total',
    description: 'Second quarter over/under',
    category: 'Quarter',
  },
  {
    key: 'totals_q3',
    label: 'Q3 Total',
    description: 'Third quarter over/under',
    category: 'Quarter',
  },
  {
    key: 'totals_q4',
    label: 'Q4 Total',
    description: 'Fourth quarter over/under',
    category: 'Quarter',
  },
  {
    key: 'team_totals_q1',
    label: 'Q1 Team Total',
    description: 'First quarter team points',
    category: 'Quarter',
  },
  {
    key: 'team_totals_q2',
    label: 'Q2 Team Total',
    description: 'Second quarter team points',
    category: 'Quarter',
  },
  {
    key: 'team_totals_q3',
    label: 'Q3 Team Total',
    description: 'Third quarter team points',
    category: 'Quarter',
  },
  {
    key: 'team_totals_q4',
    label: 'Q4 Team Total',
    description: 'Fourth quarter team points',
    category: 'Quarter',
  },
  // ── Q1 player props ──
  {
    key: 'player_points_q1',
    label: 'Q1 Points',
    description: 'First quarter player points',
    category: 'Q1 Player',
  },
  {
    key: 'player_rebounds_q1',
    label: 'Q1 Rebounds',
    description: 'First quarter player rebounds',
    category: 'Q1 Player',
  },
  {
    key: 'player_assists_q1',
    label: 'Q1 Assists',
    description: 'First quarter player assists',
    category: 'Q1 Player',
  },
];
